'use client'

import { forwardRef, useRef, useImperativeHandle, useMemo, useEffect, useState, useCallback } from 'react';
import dynamic from 'next/dynamic';

const Lottie = dynamic(() => import('lottie-react'), {
	ssr: false,
});

import LottieAnimationStyle from './style';

const LottieAnimation = forwardRef(({ animData, loop, autoplay, animateOnHover, className, isPlay, isRev }, ref) => {
	//! Refs
	const contRef = useRef();
	const lottieRef = useRef();

	useImperativeHandle(ref, () => contRef.current, []);

	//! States
	const [play, setPlay] = useState(false);
	const [isReverse, setIsReverse] = useState(false);
	const [isAnimationReady, setIsAnimationReady] = useState(false);

	//! Animation play/pause for hover
	// useEffect(() => {
	//     if (!autoplay) {
	//         if (play) {
	//             lottieRef.current.setDirection(1)
	//             lottieRef.current.play()
	//         } else if (!play && isReverse) {
	//             lottieRef.current.setDirection(-1)
	//             lottieRef.current.play()
	//         } else {
	//             lottieRef.current.pause()
	//         }
	//     }
	// }, [play, isReverse, autoplay])

	//! Check if animation is ready
	useEffect(() => {
		// Check if ref is ready and has required methods
		const checkAnimationReady = () => {
			if (lottieRef.current && 
			    typeof lottieRef.current.setDirection === 'function' && 
			    typeof lottieRef.current.play === 'function') {
				setIsAnimationReady(true);
				return true;
			}
			return false;
		};

		// Check immediately
		if (checkAnimationReady()) return;

		// If not ready, check periodically until ready
		let intervalId;
		const timeoutId = setTimeout(() => {
			if (!checkAnimationReady()) {
				// Check every 50ms until ready
				intervalId = setInterval(() => {
					if (checkAnimationReady()) {
						clearInterval(intervalId);
					}
				}, 50);
			}
		}, 100);

		return () => {
			clearTimeout(timeoutId);
			if (intervalId) clearInterval(intervalId);
		};
	}, [animData]);

	//! Animation play from parent
	useEffect(() => {
		if (!isAnimationReady || !lottieRef.current) return;
		
		// Double-check if methods exist before calling
		if (typeof lottieRef.current.setDirection !== 'function' || 
		    typeof lottieRef.current.play !== 'function') {
			return;
		}

		if (!autoplay && isPlay) {
			try {
				lottieRef.current.setDirection(1);
				lottieRef.current.play();
			} catch (error) {
				console.warn('Lottie animation error:', error);
			}
		}

		if (isRev && lottieRef.current && typeof lottieRef.current.setDirection === 'function' && typeof lottieRef.current.play === 'function') {
			try {
				lottieRef.current.setDirection(-1);
				lottieRef.current.play();
			} catch (error) {
				console.warn('Lottie animation error:', error);
			}
		}
	}, [isPlay, autoplay, isRev, isAnimationReady]);

	//! Default options
	const defaultOptions = useMemo(
		() => ({
			loop: loop,
			autoplay: autoplay,
			animationData: animData,
			rendererSettings: {
				preserveAspectRatio: 'xMidYMid slice',
			},
		}),
		[animData, loop, autoplay]
	);

	//! Handle Mouse Enter
	const onMouseEnter = useCallback(() => animateOnHover && setPlay(true), [animateOnHover]);

	//! Handle Mouse Leave
	const onMouseLeave = useCallback(() => {
		if (animateOnHover) {
			setPlay(false);
			setIsReverse(true);
		}
	}, [animateOnHover]);

	//! Handle Complete
	const onComplete = useCallback(() => {
		if (!autoplay && !isPlay && lottieRef.current && typeof lottieRef.current.pause === 'function') {
			lottieRef.current.pause();
		}
	}, [autoplay, isPlay]);

	//! Handle Loop Complete
	const onLoopComplete = useCallback(() => {
		if (animateOnHover && isReverse && lottieRef.current && typeof lottieRef.current.pause === 'function') {
			setIsReverse(false);
			lottieRef.current.pause();
		}
	}, [animateOnHover, isReverse, autoplay, isPlay]);

	return (
		<LottieAnimationStyle
			ref={contRef}
			className={`lottie-container ${className || ''}`}>
			<Lottie
				width={'auto'}
				height={'100%'}
				lottieRef={lottieRef}
				className={`lottie-wrapper`}
				onLoopComplete={onLoopComplete}
				onComplete={onComplete}
				onMouseEnter={onMouseEnter}
				onMouseLeave={onMouseLeave}
				{...defaultOptions}
			/>
		</LottieAnimationStyle>
	);
});

export default LottieAnimation;
